import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import UpcomingPostItem from './UpcomingPostItem';
import { getUpcomingPosts } from '../../lib/api';

interface Post {
  id: string;
  title: string;
  scheduledAt: string;
  platforms: Array<'linkedin' | 'instagram' | 'facebook' | 'tiktok' | 'youtube' | 'twitter'>;
}

export default function UpcomingPostsSection() {
  const { t } = useTranslation();
  const { workspaceSlug } = useParams<{ workspaceSlug: string }>();
  const [posts, setPosts] = useState<Post[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchPosts() {
      setIsLoading(true);
      setError(null);
      try {
        const data = await getUpcomingPosts(10);
        setPosts(data);
      } catch (err: any) {
        console.error('Failed to fetch upcoming posts:', err);
        setError(err.message || 'Failed to load posts');
        // Set mock data on error for demo purposes
        setPosts([
          {
            id: '1',
            title: '5 breathwork exercises to reduce stress',
            scheduledAt: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(), // 2 hours from now
            platforms: ['instagram', 'tiktok'],
          },
          {
            id: '2',
            title: 'Behind the scenes: Our morning routine',
            scheduledAt: new Date(Date.now() + 26 * 60 * 60 * 1000).toISOString(), // Tomorrow
            platforms: ['facebook'],
          },
          {
            id: '3',
            title: 'Client transformation story',
            scheduledAt: new Date(Date.now() + 50 * 60 * 60 * 1000).toISOString(), // Wednesday
            platforms: ['instagram', 'linkedin'],
          },
          {
            id: '4',
            title: 'Quick breathwork for better sleep',
            scheduledAt: new Date(Date.now() + 74 * 60 * 60 * 1000).toISOString(), // Thursday
            platforms: ['tiktok'],
          },
        ]);
      } finally {
        setIsLoading(false);
      }
    }
    fetchPosts();
  }, []);

  return (
    <div className="border-2 border-black dark:border-gray-600 rounded-lg p-6 shadow-brutal bg-white dark:bg-gray-800">
      {/* Header */}
      <h2 className="text-2xl font-bold mb-4 dark:text-gray-100">
        {t('dashboard.upcoming.title')}
      </h2>

      {/* Posts List (Scrollable) */}
      <div className="max-h-[400px] overflow-y-auto space-y-2 mb-4">
        {isLoading ? (
          // Loading skeleton
          <div className="space-y-2">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="h-12 bg-gray-100 dark:bg-gray-700 rounded-lg animate-pulse"
              />
            ))}
          </div>
        ) : error ? (
          <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
        ) : posts.length === 0 ? (
          // Empty state
          <div className="text-center py-8">
            <div className="text-4xl mb-2">📅</div>
            <p className="text-gray-500 dark:text-gray-400 font-medium">
              {t('dashboard.upcoming.empty')}
            </p>
            <Link
              to={`/${workspaceSlug}/create`}
              className="text-sm text-black dark:text-white hover:underline mt-2 inline-block"
            >
              {t('dashboard.upcoming.createFirst')}
            </Link>
          </div>
        ) : (
          // Posts list
          posts.map((post) => (
            <UpcomingPostItem key={post.id} post={post} />
          ))
        )}
      </div>

      {/* Footer Link */}
      {posts.length > 0 && (
        <div className="pt-4 border-t-2 border-gray-200 dark:border-gray-700">
          <Link
            to={`/${workspaceSlug}/calendar`}
            className="text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-black dark:hover:text-white hover:underline transition-colors inline-flex items-center gap-1"
          >
            {t('dashboard.upcoming.viewCalendar')}
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </Link>
        </div>
      )}
    </div>
  );
}
